/* eslint-disable jsx-a11y/anchor-is-valid */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import { useSettings } from '../hooks/useSettings';
import { toWhatsAppUrl } from '../api/settings';
import '../styles/DeleteRequest.css';

function DeleteRequest() {
  const [form, setForm] = useState({
    fullname: '',
    mobile: '',
    email: '',
    reason: '',
    comments: '',
    confirmed: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [refId, setRefId] = useState('');

  const isValid =
    form.fullname.trim().length >= 2 &&
    form.mobile.length === 10 &&
    form.reason &&
    form.confirmed;

  function handleMobile(e) {
    const val = e.target.value.replace(/\D/g, '').slice(0, 10);
    setForm(f => ({ ...f, mobile: val }));
  }

  function handleSubmit() {
    if (!isValid) return;
    setSubmitting(true);
    setTimeout(() => {
      const id = 'SF-DEL-' + Date.now().toString(36).toUpperCase().slice(-6);
      setRefId(id);
      setSubmitted(true);
      setSubmitting(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 1800);
  }

  const { settings } = useSettings();

  return (
    <>
      <Header />

      {/* PAGE */}
      <div className="page">

        {/* WARNING BANNER */}
        <div className="warning-banner">
          <div className="warning-icon"><i className="ti ti-alert-triangle"></i></div>
          <div>
            <div className="warning-title">This action is permanent and cannot be undone</div>
            <div className="warning-desc">Deleting your account will permanently remove all your personal data, order history, saved addresses, and reviews from our systems. You will not be able to recover this information after deletion.</div>
          </div>
        </div>

        {/* FORM CARD */}
        <div className="card">
          <div className="card-header">
            <div className="card-eyebrow"><i className="ti ti-trash" style={{fontSize:'13px'}}></i> Data deletion request</div>
            <h1 className="card-title">Delete my account &amp; data</h1>
            <p className="card-subtitle">Fill in the form below and our team will process your request within 30 days as per our privacy policy.</p>
          </div>

          <div className="card-body">
            {submitted ? (
              <div className="success-state">
                <div className="success-circle"><i className="ti ti-check"></i></div>
                <div className="success-title">Request submitted successfully</div>
                <div className="success-desc">We have received your data deletion request. Our team will process it and permanently delete your account within <strong>30 days</strong>.</div>
                <div className="success-ref">Reference ID: <strong>{refId}</strong></div>
                <br />
                <p style={{fontSize:'13px', color:'var(--text-muted)', marginBottom:'20px'}}>If you have any questions, contact us at <a href="mailto:support@sadguruflower.com">support@sadguruflower.com</a> with your reference ID.</p>
                <Link className="btn-home" to="/">
                  <i className="ti ti-home" style={{fontSize:'16px'}}></i>
                  Back to home
                </Link>
              </div>
            ) : (
              <div>
                {/* Full Name */}
                <div className="form-group">
                  <label className="form-label" htmlFor="fullname">Full name <span>*</span></label>
                  <input
                    className="form-input"
                    type="text"
                    id="fullname"
                    placeholder="Enter your full name"
                    autoComplete="name"
                    value={form.fullname}
                    onChange={e => setForm(f => ({ ...f, fullname: e.target.value }))}
                  />
                </div>

                {/* Mobile Number */}
                <div className="form-group">
                  <label className="form-label" htmlFor="mobile">Registered mobile number <span>*</span></label>
                  <div className="input-wrap">
                    <span className="input-prefix">+91</span>
                    <input
                      className="form-input with-prefix"
                      type="tel"
                      id="mobile"
                      placeholder="98765 43210"
                      maxLength={10}
                      autoComplete="tel"
                      value={form.mobile}
                      onChange={handleMobile}
                    />
                  </div>
                  <div className="form-hint">
                    <i className="ti ti-info-circle"></i>
                    This must be the mobile number you used to sign in to the Sadguru Flower app
                  </div>
                </div>

                {/* Email (optional) */}
                <div className="form-group">
                  <label className="form-label" htmlFor="email">
                    Email address <span style={{color:'var(--text-light)', fontWeight:400}}>(optional)</span>
                  </label>
                  <input
                    className="form-input"
                    type="email"
                    id="email"
                    placeholder="you@example.com"
                    autoComplete="email"
                    value={form.email}
                    onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  />
                  <div className="form-hint">
                    <i className="ti ti-info-circle"></i>
                    We will send a confirmation to this email once your data is deleted
                  </div>
                </div>

                {/* Reason */}
                <div className="form-group">
                  <label className="form-label" htmlFor="reason">Reason for deletion <span>*</span></label>
                  <select
                    className="form-input"
                    id="reason"
                    value={form.reason}
                    onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
                  >
                    <option value="" disabled>Select a reason</option>
                    <option value="no_longer_use">I no longer use the app</option>
                    <option value="privacy_concerns">Privacy concerns</option>
                    <option value="switching_service">Switching to another service</option>
                    <option value="too_many_notifications">Too many notifications</option>
                    <option value="bad_experience">Bad experience with service</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                {/* Additional comments */}
                <div className="form-group">
                  <label className="form-label" htmlFor="comments">
                    Additional comments <span style={{color:'var(--text-light)', fontWeight:400}}>(optional)</span>
                  </label>
                  <textarea
                    className="form-input"
                    id="comments"
                    placeholder="Tell us more about why you want to delete your account or share any feedback…"
                    rows={3}
                    value={form.comments}
                    onChange={e => setForm(f => ({ ...f, comments: e.target.value }))}
                  />
                </div>

                {/* Confirmation checkbox */}
                <label className="checkbox-group" htmlFor="confirm">
                  <input
                    type="checkbox"
                    id="confirm"
                    checked={form.confirmed}
                    onChange={e => setForm(f => ({ ...f, confirmed: e.target.checked }))}
                  />
                  <span className="checkbox-label">
                    <strong>I understand this action is permanent.</strong> I confirm that I want to delete my Sadguru Flower account and all associated personal data, including my order history, saved addresses, reviews, and payment records. This cannot be undone.
                  </span>
                </label>

                {/* Submit */}
                <button className="btn-danger" disabled={!isValid || submitting} onClick={handleSubmit}>
                  {submitting ? (
                    <>
                      <i className="ti ti-loader-2" style={{animation:'spin 1s linear infinite', fontSize:'18px'}}></i>
                      Submitting…
                    </>
                  ) : (
                    <>
                      <i className="ti ti-trash"></i>
                      Submit deletion request
                    </>
                  )}
                </button>

                <p style={{fontSize:'12px', color:'var(--text-light)', textAlign:'center', marginTop:'14px', lineHeight:1.6}}>
                  By submitting this form, you agree to our{' '}
                  <Link to="/privacy">Privacy Policy</Link>.
                  Requests are processed within 30 days.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* WHAT GETS DELETED */}
        <div className="card">
          <div className="card-header" style={{borderBottom:'none', paddingBottom:0}}>
            <div style={{fontSize:'15px', fontWeight:600, color:'var(--black)', display:'flex', alignItems:'center', gap:'8px'}}>
              <i className="ti ti-list" style={{fontSize:'18px', color:'var(--danger)'}}></i>
              What will be deleted
            </div>
          </div>
          <div className="card-body" style={{paddingTop:'16px'}}>
            <ul className="delete-list">
              <li><i className="ti ti-device-mobile"></i> Your mobile number and account profile</li>
              <li><i className="ti ti-map-pin"></i> All saved delivery addresses</li>
              <li><i className="ti ti-shopping-bag"></i> Complete order history and receipts</li>
              <li><i className="ti ti-star"></i> Reviews and ratings you have submitted</li>
              <li><i className="ti ti-bell"></i> Push notification preferences and tokens</li>
              <li><i className="ti ti-credit-card"></i> Payment method preferences (no card data is stored)</li>
            </ul>
          </div>
        </div>

        {/* WHAT HAPPENS NEXT */}
        <div className="card">
          <div className="card-header" style={{borderBottom:'none', paddingBottom:0}}>
            <div style={{fontSize:'15px', fontWeight:600, color:'var(--black)', display:'flex', alignItems:'center', gap:'8px'}}>
              <i className="ti ti-clock" style={{fontSize:'18px', color:'var(--brand)'}}></i>
              What happens after you submit
            </div>
          </div>
          <div className="card-body" style={{paddingTop:'20px'}}>
            <div className="timeline">
              <div className="tl-item">
                <div className="tl-dot brand"><i className="ti ti-mail"></i></div>
                <div className="tl-content">
                  <div className="tl-title">Request received</div>
                  <div className="tl-desc">You will receive a confirmation on the email you provided (if any). Our team reviews the request within 1–2 business days.</div>
                </div>
              </div>
              <div className="tl-item">
                <div className="tl-dot brand"><i className="ti ti-user-check"></i></div>
                <div className="tl-content">
                  <div className="tl-title">Identity verified</div>
                  <div className="tl-desc">We may send an OTP to your registered mobile number to confirm your identity before proceeding.</div>
                </div>
              </div>
              <div className="tl-item">
                <div className="tl-dot danger"><i className="ti ti-trash"></i></div>
                <div className="tl-content">
                  <div className="tl-title">Data permanently deleted</div>
                  <div className="tl-desc">All your personal data is erased from our systems within 30 days of request submission.</div>
                </div>
              </div>
              <div className="tl-item">
                <div className="tl-dot gray"><i className="ti ti-check"></i></div>
                <div className="tl-content">
                  <div className="tl-title">Confirmation sent</div>
                  <div className="tl-desc">A final confirmation is sent to your email address once deletion is complete.</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CONTACT */}
        <p style={{fontSize:'13px', color:'var(--text-muted)', textAlign:'center', margin:'28px 0 16px'}}>Need help or want to cancel your request? Contact us directly.</p>

        <div className="contact-card">
          <div className="contact-icon"><i className="ti ti-brand-whatsapp"></i></div>
          <div className="contact-txt">
            <div className="ct1">WhatsApp or Call — 7 days a week</div>
            <div className="ct2"><a href={toWhatsAppUrl(settings.whatsappNumber)}>{settings.whatsappNumber}</a></div>
          </div>
        </div>

        <div className="contact-card">
          <div className="contact-icon"><i className="ti ti-mail"></i></div>
          <div className="contact-txt">
            <div className="ct1">Email support</div>
            <div className="ct2"><a href="mailto:support@sadguruflower.com">support@sadguruflower.com</a></div>
          </div>
        </div>

      </div>

      {/* FOOTER */}
      <footer className="dr-footer">
        <p>© 2026 Sadguru Flower. All rights reserved.</p>
        <div className="footer-links">
          <Link to="/">Home</Link>
          <Link to="/privacy">Privacy policy</Link>
          <a href="#">Terms of service</a>
        </div>
      </footer>
    </>
  );
}

export default DeleteRequest;
